/**
 * ProtocolParticles Component - Enhanced
 * ======================================
 * More particles + blockchain node connections
 */

import React, { useEffect, useRef, useCallback } from 'react';

const ProtocolParticles = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);
  const nodesRef = useRef([]);

  class Particle {
    constructor(canvas) {
      this.canvas = canvas;
      this.reset(true);
    }

    reset(initial = false) {
      this.x = Math.random() * this.canvas.width;
      this.y = initial ? Math.random() * this.canvas.height : this.canvas.height + 10;
      this.size = Math.random() * 2.5 + 0.5;
      this.speedY = -(Math.random() * 0.5 + 0.1);
      this.speedX = (Math.random() - 0.5) * 0.3;
      this.opacity = Math.random() * 0.6 + 0.2;
      this.color = `rgba(212, 168, 83, ${this.opacity})`;
    }

    update() {
      this.y += this.speedY;
      this.x += this.speedX;
      
      // Slight wave motion
      this.x += Math.sin(this.y * 0.01) * 0.2;

      if (this.y < -10 || this.x < -10 || this.x > this.canvas.width + 10) {
        this.reset();
      }
    }

    draw(ctx) {
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.fillStyle = this.color;
      ctx.fill();
    }
  }

  // Blockchain node class
  class Node {
    constructor(canvas) {
      this.canvas = canvas;
      this.x = Math.random() * canvas.width;
      this.y = Math.random() * canvas.height;
      this.size = Math.random() * 3 + 2;
      this.speedX = (Math.random() - 0.5) * 0.3;
      this.speedY = (Math.random() - 0.5) * 0.3;
      this.opacity = Math.random() * 0.4 + 0.1;
    }

    update() {
      this.x += this.speedX;
      this.y += this.speedY;

      // Bounce off edges
      if (this.x < 0 || this.x > this.canvas.width) this.speedX *= -1;
      if (this.y < 0 || this.y > this.canvas.height) this.speedY *= -1;
    }

    draw(ctx) {
      // Draw node
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(212, 168, 83, ${this.opacity})`;
      ctx.fill();
      
      // Node glow
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size * 2, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(212, 168, 83, ${this.opacity * 0.2})`;
      ctx.fill();
    }
  }

  const initElements = useCallback((canvas) => {
    const isMobile = window.innerWidth < 768;
    
    // More particles
    const particleCount = isMobile ? 60 : 120;
    particlesRef.current = [];
    for (let i = 0; i < particleCount; i++) {
      particlesRef.current.push(new Particle(canvas));
    }

    // Blockchain nodes
    const nodeCount = isMobile ? 8 : 15;
    nodesRef.current = [];
    for (let i = 0; i < nodeCount; i++) {
      nodesRef.current.push(new Node(canvas));
    }
  }, []);

  // Draw connections between nearby nodes
  const drawConnections = useCallback((ctx) => {
    const nodes = nodesRef.current;
    const maxDistance = 150;

    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const dx = nodes[i].x - nodes[j].x;
        const dy = nodes[i].y - nodes[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < maxDistance) {
          const opacity = (1 - distance / maxDistance) * 0.15;
          ctx.beginPath();
          ctx.moveTo(nodes[i].x, nodes[i].y);
          ctx.lineTo(nodes[j].x, nodes[j].y);
          ctx.strokeStyle = `rgba(212, 168, 83, ${opacity})`;
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }
    }
  }, []);

  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    // Clear with slight fade for trail effect
    ctx.fillStyle = 'rgba(3, 3, 3, 0.15)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw connections first
    drawConnections(ctx);

    // Update and draw nodes
    nodesRef.current.forEach(node => {
      node.update();
      node.draw(ctx);
    });

    // Update and draw particles
    particlesRef.current.forEach(particle => {
      particle.update();
      particle.draw(ctx);
    });

    animationRef.current = requestAnimationFrame(animate);
  }, [drawConnections]);

  const handleResize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    initElements(canvas);
  }, [initElements]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    initElements(canvas);
    animate();

    window.addEventListener('resize', handleResize);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [initElements, animate, handleResize]);

  return (
    <canvas
      ref={canvasRef}
      className="protocol-particles-canvas"
      aria-hidden="true"
    />
  );
};

export default ProtocolParticles;
